// Pure workbook-spec building for the Set Summary page's Excel download.
// Zero imports on purpose (same pattern as collectionExport.mjs): everything
// app-specific (the summary row, the set data file, exceljs) is injected by
// the runner in setSummaryExportRun.js, so `node --test` can import this file
// directly.
//
// The builder returns a neutral "workbook spec" — plain objects describing
// sheets, rows, and style hints — and the runner translates that spec into a
// styled exceljs workbook. Keeping exceljs out of this module keeps the tests
// dependency-free and the styling swappable.
//
// card_key grammar (frozen; see src/lib/cardKey.js):
//   <setId>__<subSetId>__<cardN>

// --- Palette (mirrors the SetSummary page's C tokens, opaque hex) ---
export const PALETTE = {
  ink: '#0A0A16',
  panel: '#141420',
  headFill: '#2A2A3E',
  gold: '#F0C040',
  cyan: '#00D4FF',
  green: '#00E87A',
  purple: '#B060FF',
  red: '#FF4040',
  stripe: '#F4F6FA',
  border: '#D9DEE8',
  text: '#1A1A2A',
  muted: '#9AA3B5',
};

const STAT_COLOR = {
  gold: PALETTE.gold, cyan: PALETTE.cyan, green: PALETTE.green,
  purple: PALETTE.purple, red: PALETTE.red,
};
const TIER_COLOR = { normal: PALETTE.cyan, rare: PALETTE.purple, mythic: PALETTE.gold };

// --- Small pure helpers ---

// print_run -> display label. null/undefined = unnumbered.
export const prLabel = (pr) =>
  pr === null || pr === undefined ? 'Unnumbered' : pr === 1 ? '1/1' : `/${pr}`;

export const cardKeyFor = (setId, subSetId, n) => `${setId}__${subSetId}__${n}`;

// First hex color in a CSS gradient string — the exportable stand-in for the
// summary rainbow's gradients (Excel can't render CSS gradients).
export const repColorFromGradient = (gradient) => {
  const m = String(gradient || '').match(/#([0-9a-fA-F]{6})/);
  return m ? `#${m[1].toUpperCase()}` : null;
};

// "DUEL ON CLOUD CITY — 15 CARDS" -> "Duel on Cloud City"
export const sectionDisplayName = (title) => String(title || '').split('—')[0].trim();

const SMALL_WORDS = new Set(['of', 'the', 'on', 'in', 'a', 'an', 'and', 'to', 'at']);
export const titleCase = (s) =>
  String(s).toLowerCase().split(/\s+/).map((w, i) =>
    i > 0 && SMALL_WORDS.has(w) ? w : w.charAt(0).toUpperCase() + w.slice(1)
  ).join(' ');

// Excel tab names: no [ ] : * ? / \ and max 31 chars. Word-boundary trim
// before a hard slice so "Topps Chrome Layers Original Art" degrades to
// "Topps Chrome Layers Original" rather than "...Original Ar".
export const sanitizeTabName = (name) => {
  let t = String(name || 'Sheet').replace(/[[\]:*?/\\]/g, '').trim();
  if (t.length > 31) {
    const cut = t.slice(0, 31);
    const lastSpace = cut.lastIndexOf(' ');
    t = (lastSpace >= 20 ? cut.slice(0, lastSpace) : cut).trim();
  }
  return t || 'Sheet';
};

// Dedupe within a workbook: "Name", "Name 2", "Name 3"… staying under the cap.
export const uniqueTabName = (name, taken) => {
  let t = sanitizeTabName(name);
  if (!taken.has(t.toLowerCase())) { taken.add(t.toLowerCase()); return t; }
  for (let i = 2; ; i++) {
    const suffix = ` ${i}`;
    const candidate = sanitizeTabName(t.slice(0, 31 - suffix.length)) + suffix;
    if (!taken.has(candidate.toLowerCase())) { taken.add(candidate.toLowerCase()); return candidate; }
  }
};

export const exportFilename = (setId, dateISO) =>
  `collectionkeeper_${setId}_summary_${dateISO}.xlsx`;

// Per-type parallel fallbacks, mirroring SetSummary.js loadSectionParallels:
// a section's inline parallels win; otherwise base/insert/auto fall back to
// the file-level arrays. Sketch sections never fall back (they are 1/1s).
export const parallelsForSection = (section, fileParallels) => {
  if (section.parallels?.length) return section.parallels;
  if (section.type === 'sketch') return [];
  const { PARALLELS, INSERT_PARALLELS, AUTO_PARALLELS } = fileParallels || {};
  const fallback = {
    base: PARALLELS,
    insert: INSERT_PARALLELS || PARALLELS,
    auto: AUTO_PARALLELS || PARALLELS,
  };
  return fallback[section.type] || [];
};

// --- Row constructors (spec vocabulary the runner understands) ---
const banner = (text, cols, sub) => ({ kind: 'banner', text, cols, sub });
const section = (text, cols, accent) => ({ kind: 'section', text, cols, accent: accent || PALETTE.gold });
// freeze: pin the view below this header row. filter: add an autofilter on it.
// Row-level flags (not sheet-level indexes) because banners expand to two
// spreadsheet rows, so spec indexes and sheet row numbers diverge.
const header = (cells, opts = {}) => ({ kind: 'header', cells, ...opts });
const data = (cells, opts = {}) => ({ kind: 'data', cells, ...opts });
const blank = () => ({ kind: 'blank' });
const note = (text, cols) => ({ kind: 'note', text, cols });

const CARD_COLUMNS = [12, 44, 28, 40];

function pushParallelLegend(rows, parallels, accent) {
  rows.push(section(`PARALLELS — ${parallels.length}`, 4, accent || PALETTE.cyan));
  rows.push(header(['', 'PARALLEL', 'PRINT RUN', '']));
  parallels.forEach((p, i) => {
    rows.push(data(['', p.name || 'Numbered Base', prLabel(p.print_run), ''], {
      stripe: i % 2 === 1, swatch: p.color || null,
    }));
  });
}

function cardTabSheet({ setId, sec, tabName, tabColor, artistHeader, setLine, fileParallels }) {
  const rows = [];
  rows.push(banner(sec.title, 4, `${sec.cards.length} cards · ${setLine}`));
  const parallels = parallelsForSection(sec, fileParallels);
  if (parallels.length) pushParallelLegend(rows, parallels);
  rows.push(section('CHECKLIST', 4));
  rows.push(header(['CARD #', 'CARD NAME', artistHeader, 'CARD KEY'], { freeze: true }));
  sec.cards.forEach((c, i) => {
    rows.push(data([c.n, c.name, c.artist || '', cardKeyFor(setId, sec.id, c.n)], {
      stripe: i % 2 === 1, mono: [4],
    }));
  });
  return { name: tabName, tabColor, columns: CARD_COLUMNS, rows };
}

// --- The main builder ---
// summary: camelCase summary shape from SetSummary.rowToSummary (or the local
//          SET_SUMMARIES entry); may be null → summary tab is skipped.
// sets:    the data file's SETS array; may be null → card tabs are skipped.
// fileParallels: { PARALLELS, INSERT_PARALLELS, AUTO_PARALLELS } from the file.
// dateISO: YYYY-MM-DD stamp for the footer note.
export function buildWorkbookSpec({ setId, summary, sets, fileParallels, dateISO }) {
  const sheets = [];
  const taken = new Set();
  const setLine = summary
    ? `${summary.year} ${summary.brand} ${summary.title}`.trim()
    : setId;

  // ---- Tab 1: Summary ----
  if (summary) {
    const rows = [];
    rows.push(banner(
      `${summary.year} ${summary.brand} — ${summary.title}`.trim(), 4,
      `RELEASED ${summary.releaseDate || 'TBD'} · collectionkeeper.org/resources/sets/${setId}`
    ));
    if (summary.lede) { rows.push(blank()); rows.push({ kind: 'lede', text: summary.lede, cols: 4 }); }

    if (summary.stats?.length) {
      rows.push(section('SET AT A GLANCE', 4));
      rows.push(header(['', 'STAT', '#', 'DETAIL']));
      summary.stats.forEach((s, i) => rows.push(data(['', s.label, s.num, s.sub || ''], {
        stripe: i % 2 === 1, swatch: STAT_COLOR[s.color] || PALETTE.panel, big: [3],
      })));
    }
    if (summary.hobbyBox?.length) {
      rows.push(section('HOBBY BOX CONFIGURATION', 4));
      rows.push(header(['', 'CONFIG', '#', '']));
      summary.hobbyBox.forEach((b, i) => rows.push(data(['', b.l, b.v, ''], {
        stripe: i % 2 === 1, big: [3],
      })));
    }
    if (summary.pullRates?.length) {
      rows.push(section('INSERT PULL RATES', 4));
      rows.push(header(['', 'INSERT', 'ODDS', 'TIER']));
      summary.pullRates.forEach((p, i) => rows.push(data(['', p.name, p.odds, (p.tier || '').toUpperCase()], {
        stripe: i % 2 === 1, swatch: TIER_COLOR[p.tier] || PALETTE.panel,
      })));
    }
    if (summary.parallelRainbow?.length) {
      rows.push(section('BASE PARALLEL RAINBOW', 4));
      rows.push(header(['', 'PARALLEL', 'PRINT RUN', '']));
      summary.parallelRainbow.forEach((p, i) => rows.push(data(['', p.name, p.pr, ''], {
        stripe: i % 2 === 1, swatch: repColorFromGradient(p.gradient) || PALETTE.panel,
      })));
    }
    if (summary.artists?.length) {
      rows.push(section('COMMISSIONED ARTISTS', 4));
      rows.push(header(['', 'ARTIST', '', 'WORK']));
      summary.artists.forEach((a, i) => rows.push(data(['', a.name, '', a.work || ''], {
        stripe: i % 2 === 1,
      })));
    }
    rows.push(blank());
    rows.push(note(`Downloaded from CollectionKeeper.org — the free Star Wars trading card platform. Data as of ${dateISO}.`, 4));

    sheets.push({
      name: uniqueTabName('Summary', taken), tabColor: PALETTE.gold,
      columns: [4, 30, 14, 44], rows,
    });
  }

  if (!sets?.length) return { sheets, filename: exportFilename(setId, dateISO) };

  // ---- Tab 2: base section(s) ----
  for (const sec of sets.filter((s) => s.type === 'base')) {
    sheets.push(cardTabSheet({
      setId, sec, setLine, fileParallels,
      tabName: uniqueTabName('Base Set', taken), tabColor: PALETTE.gold,
      artistHeader: 'ARTIST',
    }));
  }

  // ---- Tabs 3..N: one per insert set ----
  for (const sec of sets.filter((s) => s.type === 'insert')) {
    sheets.push(cardTabSheet({
      setId, sec, setLine, fileParallels,
      tabName: uniqueTabName(titleCase(sectionDisplayName(sec.title)), taken),
      tabColor: PALETTE.cyan, artistHeader: 'ARTIST / SUBJECT',
    }));
  }

  // ---- Tab N+1: all autograph tiers on one tab ----
  const autos = sets.filter((s) => s.type === 'auto');
  if (autos.length) {
    const rows = [];
    const total = autos.reduce((n, s) => n + s.cards.length, 0);
    rows.push(banner('AUTOGRAPHS', 4,
      `${total} cards across ${autos.length} ${autos.length === 1 ? 'tier' : 'tiers'} · ${setLine}`));
    for (const sec of autos) {
      rows.push(section(sec.title, 4, PALETTE.green));
      const parallels = parallelsForSection(sec, fileParallels);
      if (parallels.length) pushParallelLegend(rows, parallels, PALETTE.green);
      rows.push(blank());
      rows.push(header(['CARD #', 'SIGNER', 'CHARACTER', 'CARD KEY']));
      sec.cards.forEach((c, i) => {
        rows.push(data([c.n, c.name, c.artist || '', cardKeyFor(setId, sec.id, c.n)], {
          stripe: i % 2 === 1, mono: [4],
        }));
      });
    }
    sheets.push({
      name: uniqueTabName('Autographs', taken), tabColor: PALETTE.green,
      columns: CARD_COLUMNS, rows,
    });
  }

  // ---- Tab N+2: sketch artist roster ----
  for (const sec of sets.filter((s) => s.type === 'sketch')) {
    const rows = [];
    rows.push(banner(sec.title, 4, 'All sketch cards are one-of-one originals (1/1)'));
    rows.push(section('ARTIST ROSTER', 4, PALETTE.purple));
    rows.push(header(['CARD #', 'ARTIST', 'PRINT RUN', 'CARD KEY'], { freeze: true }));
    sec.cards.forEach((c, i) => {
      rows.push(data([c.n, c.name, '1/1', cardKeyFor(setId, sec.id, c.n)], {
        stripe: i % 2 === 1, mono: [4],
      }));
    });
    sheets.push({
      name: uniqueTabName('Sketch Artists', taken), tabColor: PALETTE.purple,
      columns: [12, 36, 20, 40], rows,
    });
  }

  // ---- Tab N+3: the entire set flat ----
  {
    const rows = [];
    const total = sets.reduce((n, s) => n + s.cards.length, 0);
    rows.push(banner('FULL SET — EVERY CARD', 6,
      `${total} cards · base + inserts + autographs + sketch roster`));
    rows.push(blank());
    rows.push(header(['SECTION', 'TYPE', 'CARD #', 'CARD NAME', 'ARTIST / SUBJECT', 'CARD KEY'],
      { freeze: true, filter: true }));
    let i = 0;
    for (const sec of sets) {
      const secName = sectionDisplayName(sec.title);
      for (const c of sec.cards) {
        rows.push(data([secName, (sec.type || '').toUpperCase(), c.n, c.name, c.artist || '', cardKeyFor(setId, sec.id, c.n)], {
          stripe: i % 2 === 1, mono: [6],
        }));
        i++;
      }
    }
    sheets.push({
      name: uniqueTabName('Full Set', taken), tabColor: PALETTE.red,
      columns: [32, 10, 12, 44, 28, 40], rows,
    });
  }

  return { sheets, filename: exportFilename(setId, dateISO) };
}
